import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Renders a prominent eligibility-status banner above the Analytics detail
 * views (Errors, Rankings) when a forecaster has crossed the missed-day
 * allowance for the month. The banner explains that league rewards are
 * forfeit for the period but daily-rank rewards remain available — the
 * detail metrics underneath continue to render normally.
 *
 * Returns null when the user is still within the allowance, so callers can
 * render it unconditionally.
 *
 * @param {{
 *   nDaysWithPenalties?: number | null,
 *   maxMissedDays?: number | null,
 *   className?: string,
 * }} props
 */
export default function LeagueIneligibilityBanner({
  nDaysWithPenalties,
  maxMissedDays = 5,
  className = '',
}) {
  const limit = maxMissedDays ?? 5;
  const missed = nDaysWithPenalties ?? 0;
  if (missed <= limit) return null;

  const overLimit = missed - limit;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-700/60 dark:bg-amber-900/20 ${className}`.trim()}
    >
      <ExclamationTriangleIcon
        aria-hidden="true"
        className="mt-0.5 h-5 w-5 flex-none text-amber-600 dark:text-amber-400"
      />
      <div className="min-w-0 flex-1 text-sm leading-snug">
        <p className="font-semibold text-amber-800 dark:text-amber-200">
          Not evaluated for league rewards this month
        </p>
        <p className="mt-0.5 text-amber-700/90 dark:text-amber-300/90">
          {missed} missed {missed === 1 ? 'day' : 'days'} ·{' '}
          {overLimit} {overLimit === 1 ? 'day' : 'days'} over the {limit}-day allowance.
          Daily-rank rewards still apply on the days you submitted.
        </p>
      </div>
    </div>
  );
}
